import { getCookie } from '@/modules/common';
import { AddToCartItem, EventType } from '@/modules/models';
import { bridgeAtom, store } from '@/store';
import { getProductIdType } from '@/utils';
import * as HttpClient from './api.HttpClient';
import { getUid, isInfluencerUser, isMLStagingUser, isTestUser } from './data';
import * as types from './types';

export const getShopifyCart = () => HttpClient.getShopifyCart();

export const getCartToken = async () => {
  try {
    const cookieToken = getCookie('cart');

    if (cookieToken?.length) {
      return cookieToken;
    }

    const cart = await getShopifyCart();

    return cart?.token || '';
  } catch (_e) {
    return '';
  }
};

export const getSessionData = async (): Promise<types.SessionData> => {
  const uid = getUid();
  const isTest = isTestUser();
  const isInfluencer = isInfluencerUser();

  const token = await getCartToken();
  const { countryCode = '', localization = '', location = null } = store.get(bridgeAtom);
  const isMLStagingParam = new URLSearchParams(window.location.search).get('isMLStaging') === '1';
  if (isMLStagingParam) {
    sessionStorage.setItem('isMLStaging', '1');
  }
  const isMLStaging = isMLStagingUser();

  return {
    uid,
    token,
    isTestUser: isTest,
    isInfluencer,
    userAgent: window.navigator.userAgent,
    customerId: window.loggedUserId ?? null,
    countryCode,
    localization,
    location,
    isStaging: isMLStaging,
  };
};

interface AddShopifyEventProps {
  type: EventType;
  handle: string;
  quantity: number;
  userSession: types.SessionData;
}

export const addShopifyEvent = async ({ userSession, type, handle, quantity }: AddShopifyEventProps) => {
  await HttpClient.addShopifyEvent({
    userSession,
    type,
    metadata: {
      quantity,
      productId: handle,
    },
  });
};

type AddEventProps = {
  type: EventType;
  userSession: types.SessionData;
  metadata: Record<string, any>;
};

export const addEvent = async ({ userSession, type, metadata }: AddEventProps) => {
  await HttpClient.addEvent({
    userSession,
    type,
    metadata,
  });
};

export const addProductToCart = async ({
  handle,
  variantId,
  quantity,
  sellingPlan,
  params,
  lineItemProperties,
}: {
  handle: string;
  variantId: string;
  quantity: number;
  sellingPlan?: string;
  params?: { [key: string]: any };
  lineItemProperties?: AddToCartItem['line_item_properties'];
}) => {
  if (!handle) {
    console.error('Saywhatt:addProductToCart - product handle is required');
    return null;
  }

  let resolvedVariantId: string | undefined = variantId;
  if (!resolvedVariantId) {
    try {
      const shopifyProduct = await HttpClient.getProducts(handle);
      resolvedVariantId = shopifyProduct?.variants?.[0]?.id;
    } catch (e) {
      console.error('Saywhatt:addProductToCart - failed to fetch product for handle:', handle, e);
      return null;
    }
    if (!resolvedVariantId) {
      console.error('Saywhatt:addProductToCart - could not resolve variant id for handle:', handle);
      return null;
    }
  }

  return HttpClient.addProductToCart({
    variantId: resolvedVariantId,
    quantity,
    params,
    lineItemProperties,
    sellingPlan,
  });
};

export const getToken = HttpClient.getToken;
export const reOpenChat = HttpClient.reOpenChat;
export const chatRate = HttpClient.chatRate;

export const messages = (params: ConvertKeysToCamelCase<types.MessagesType>) => {
  return HttpClient.messages({
    ...params,
    page_type: params.pageType,
    product_id: params.productId,
    variant_id: params.variantId,
  });
};

export const getLiveAgentAvailability = HttpClient.getLiveAgentAvailability;

export const preloadAttachments = async (files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return await HttpClient.preloadAttachments(formData);
};

export const viewHistory = async (params: types.ViewHistoryType) => {
  const { productId, pageType, abGroup, abTestName, mlAbGroup, mlAbTestName } = params;

  const { product_id: id, pageType: type } = getProductIdType({
    product_id: productId,
    pageType,
  });

  try {
    await HttpClient.viewHistory({
      productId: id,
      pageType: type,
      abGroup,
      abTestName,
      mlAbGroup,
      mlAbTestName,
    });
  } catch (e) {
    console.error('[SAYWHATT] Failed to receive history', e);
  }
};

export const updateSessionShopify = async (shopifySession?: types.SessionData | null) => {
  if (!shopifySession) {
    shopifySession = await getSessionData();
  }

  await HttpClient.updateSessionShopify(shopifySession);
};
